/**
 * Test Call Modal — Let clients test their AI agent
 * Inputs: phone number, first name
 * Hard cap: 5 test calls per day
 */

import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';

interface TestCallModalProps {
  isOpen: boolean;
  onClose: () => void;
  remaining: number;
  clientId?: string | null; // Admin impersonation
}

export function TestCallModal({ isOpen, onClose, remaining, clientId }: TestCallModalProps) {
  const [phone, setPhone] = useState('');
  const [firstName, setFirstName] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  if (!isOpen) return null;

  const handleClose = () => {
    setPhone('');
    setFirstName('');
    setResult(null);
    onClose();
  };

  const handleSend = async () => {
    if (!phone || !firstName) return;
    setSending(true);
    setResult(null);

    try {
      const fn = httpsCallable(functions, 'requestTestCall');
      const response = await fn({
        phone,
        firstName,
        ...(clientId ? { clientId } : {}),
      });
      const data = response.data as { agentName: string; remaining: number };
      setResult({
        success: true,
        message: `Your agent is calling you now! (${data.remaining} test calls remaining today)`,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      setResult({ success: false, message });
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }} onClick={handleClose}>
      <div style={{
        background: 'var(--dark-2)', borderRadius: '12px', padding: '32px',
        maxWidth: '420px', width: '90%', border: '1px solid var(--dark-3)',
      }} onClick={e => e.stopPropagation()}>

        <h2 style={{ margin: '0 0 4px', color: 'var(--white)' }}>Test My Agent</h2>
        <p style={{ color: 'var(--gray-400)', fontSize: '0.85rem', marginBottom: '20px' }}>
          Your agent will call the number below so you can hear how it sounds.
        </p>

        {/* Remaining indicator */}
        <div style={{
          padding: '8px 12px', borderRadius: '6px', marginBottom: '16px',
          background: remaining > 0 ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
          border: `1px solid ${remaining > 0 ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
          color: remaining > 0 ? '#10b981' : '#dc2626',
          fontSize: '0.8rem', textAlign: 'center',
        }}>
          {remaining > 0
            ? `${remaining} test call${remaining !== 1 ? 's' : ''} remaining today`
            : 'Daily test call limit reached — try again tomorrow'}
        </div>

        {remaining > 0 && !result?.success && (
          <>
            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label style={{ color: 'var(--gray-300)', fontSize: '0.85rem' }}>Your Phone Number</label>
              <input
                type="tel"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                placeholder="(214) 555-1234"
                style={{
                  width: '100%', padding: '10px', background: 'var(--dark)',
                  border: '1px solid var(--dark-3)', borderRadius: '6px',
                  color: 'var(--white)', fontFamily: 'inherit', fontSize: '1rem',
                }}
              />
            </div>
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label style={{ color: 'var(--gray-300)', fontSize: '0.85rem' }}>First Name (agent will use this)</label>
              <input
                type="text"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                placeholder="Your Name"
                style={{
                  width: '100%', padding: '10px', background: 'var(--dark)',
                  border: '1px solid var(--dark-3)', borderRadius: '6px',
                  color: 'var(--white)', fontFamily: 'inherit', fontSize: '1rem',
                }}
              />
            </div>

            <button
              onClick={handleSend}
              disabled={sending || !phone || !firstName}
              className="btn btn-primary"
              style={{ width: '100%', marginBottom: '10px', padding: '12px' }}
            >
              {sending ? 'Calling...' : 'Send Test Call'}
            </button>
          </>
        )}

        {result && (
          <div style={{
            padding: '12px 14px', borderRadius: '8px', marginBottom: '12px',
            background: result.success ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
            border: `1px solid ${result.success ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
            color: result.success ? '#10b981' : '#dc2626',
            fontSize: '0.85rem', textAlign: 'center',
          }}>
            {result.message}
          </div>
        )}

        <button onClick={handleClose} className="btn btn-secondary" style={{ width: '100%' }}>
          {result?.success ? 'Done' : 'Cancel'}
        </button>
      </div>
    </div>
  );
}
